if (!customElements.get('subscription-picker')) {
  customElements.define(
    'subscription-picker',
    class SubscriptionPicker extends HTMLElement {
      constructor() {
        super()
        this.product = null
        this.selectedVariant = null
        this.selectedSellingPlan = null
        this.availableSellingPlanGroups = []
        this.checkbox = null
        this.frequencyContainer = null
        this.frequencyButtons = []
        this.sellingPlanInput = null
        this.productForm = null
      }

      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init())
        } else {
          this.init()
        }
      }

      init() {
        this.loadProductData()
        this.findProductForm()
        this.setupVariantListener()
      }

      loadProductData() {
        const productJson = this.querySelector('[data-product-json]')
        if (!productJson) {
          console.warn('SubscriptionPicker: No product JSON found')
          return
        }

        try {
          this.product = JSON.parse(productJson.textContent)
          if (!this.product) {
            console.warn('SubscriptionPicker: Product data is null')
            return
          }

          this.selectedVariant = this.product.selected_or_first_available_variant || this.product.variants?.[0]
          if (!this.selectedVariant) {
            console.warn('SubscriptionPicker: No variant found')
            return
          }

          this.updateAvailableSellingPlanGroups()
          this.render()
        } catch (error) {
          console.error('SubscriptionPicker: Failed to parse product data', error)
        }
      }

      updateAvailableSellingPlanGroups() {
        if (!this.product || !this.selectedVariant) return

        this.availableSellingPlanGroups = this.product.selling_plan_groups.filter((group) =>
          group.selling_plans.some((plan) =>
            this.selectedVariant.selling_plan_allocations?.some((allocation) => allocation.selling_plan_id === plan.id)
          )
        )
      }

      getAvailableSellingPlans(group) {
        if (!this.selectedVariant) return []
        const availablePlans = group.selling_plans.filter((plan) =>
          this.selectedVariant.selling_plan_allocations?.some((allocation) => allocation.selling_plan_id === plan.id)
        )

        // Dedupe based on name
        const seenNames = new Set()
        const dedupedPlans = availablePlans.filter((plan) => {
          const name = plan.name.toLowerCase()
          if (seenNames.has(name)) {
            return false
          }
          seenNames.add(name)
          return true
        })

        // Normalize plan names - add "every" if not present
        return dedupedPlans.map((plan) => {
          const normalizedPlan = { ...plan }
          const nameLower = plan.name.toLowerCase()
          if (!nameLower.includes('every')) {
            normalizedPlan.name = `Every ${plan.name}`
          }
          return normalizedPlan
        })
      }

      setupVariantListener() {
        const productInfo = this.closest('.product__info-container')
        const sectionId = this.dataset.sectionId || productInfo?.dataset.section
        const formId = this.dataset.productFormId || (sectionId ? `product-form-${sectionId}` : null)

        if (formId) {
          this.productForm = document.getElementById(formId)

          if (this.productForm) {
            const variantInput = this.productForm.querySelector('[name="id"]')
            if (variantInput) {
              variantInput.addEventListener('change', () => {
                const variantId = parseInt(variantInput.value)
                this.selectedVariant = this.product.variants.find((v) => v.id === variantId)
                this.updateAvailableSellingPlanGroups()
                this.render()
                this.updatePrice()
              })
            }
          }
        }
      }

      findProductForm() {
        const productInfo = this.closest('.product__info-container')
        const sectionId = this.dataset.sectionId || productInfo?.dataset.section
        const formId = this.dataset.productFormId || (sectionId ? `product-form-${sectionId}` : null)

        if (formId) {
          const tryFindForm = () => {
            this.productForm = document.getElementById(formId)

            if (this.productForm && this.sellingPlanInput) {
              const existingInput = this.productForm.querySelector('input[name="selling_plan"]')
              if (existingInput && existingInput !== this.sellingPlanInput) {
                existingInput.remove()
              }
              if (this.sellingPlanInput.parentNode !== this.productForm) {
                this.productForm.appendChild(this.sellingPlanInput)
              }
            } else if (!this.productForm) {
              setTimeout(tryFindForm, 100)
            }
          }

          tryFindForm()
        }
      }

      render() {
        if (!this.product) {
          console.warn('SubscriptionPicker: No product data available')
          return
        }

        if (this.availableSellingPlanGroups.length === 0) {
          console.warn('SubscriptionPicker: No available selling plan groups for variant', this.selectedVariant?.id)
          return
        }

        const firstGroup = this.availableSellingPlanGroups[0]
        const availablePlans = this.getAvailableSellingPlans(firstGroup)

        if (availablePlans.length === 0) {
          console.warn('SubscriptionPicker: No available plans in group', firstGroup.name)
          return
        }

        const firstPlan = availablePlans[0]
        const discountPercentage = this.getDiscountPercentage(firstPlan)
        const productJsonScript = this.querySelector('[data-product-json]')
        const productJsonHtml = productJsonScript ? productJsonScript.outerHTML : ''
        const pickerId = `subscription-picker-${this.dataset.sectionId || this.dataset.productId || this.product.id}`
        const checkboxId = `${pickerId}-checkbox`
        const checkboxLabelId = `${pickerId}-label`
        const frequencyGroupId = `${pickerId}-frequencies`

        this.innerHTML = `
          ${productJsonHtml}
          <input type="hidden" name="selling_plan" value="">
          <div class="subscription-picker__checkbox-wrapper flex items-center gap-2">
            <input
              type="checkbox"
              id="${checkboxId}"
              class="subscription-picker__checkbox w-5 h-5 rounded border-2 border-black focus:ring-black cursor-pointer"
              aria-label="Subscribe and save"
              aria-labelledby="${checkboxLabelId}"
              aria-controls="${frequencyGroupId}"
              aria-expanded="false"
            >
            <label
              for="${checkboxId}"
              id="${checkboxLabelId}"
              class="subscription-picker__label text-sm cursor-pointer"
              style="font-family: var(--font-primary);"
            >
              ${discountPercentage > 0 ? `Save ${discountPercentage}% with Subscription` : 'Save with Subscription'}
            </label>
          </div>
          <div
            id="${frequencyGroupId}"
            class="subscription-picker__frequencies subscription-picker__frequencies--hidden flex flex-wrap gap-2"
            role="group"
            aria-label="Subscription frequency"
            aria-hidden="true"
          >
            ${availablePlans
              .map(
                (plan) => `
              <button
                type="button"
                class="subscription-picker__frequency px-2 py-1 text-sm font-semibold border-2 border-black rounded-full hover:border-black transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                data-plan-id="${plan.id}"
                aria-pressed="false"
                aria-label="Select ${this.getFrequencyText(plan)}"
                tabindex="-1"
              >
                ${this.getFrequencyText(plan)}
              </button>
            `
              )
              .join('')}
          </div>
          <style>
            .subscription-picker__checkbox {
              display: inline-block !important;
              visibility: visible !important;
              opacity: 1 !important;
              accent-color: black;
              -webkit-appearance: checkbox;
              appearance: checkbox;
            }
            .subscription-picker__checkbox:checked {
              accent-color: black;
              background-color: black;
            }
            input[type="checkbox"].subscription-picker__checkbox:checked {
              background-color: black;
              border-color: black;
              accent-color: black;
            }
            .subscription-picker__checkbox:focus-visible,
            .subscription-picker__frequency:focus-visible {
              outline: 2px solid black;
              outline-offset: 2px;
            }
            .subscription-picker__frequency {
              background-color: transparent;
              color: black;
            }
            .subscription-picker__frequency--selected {
              background-color: black !important;
              color: white !important;
              border-color: black !important;
            }
            .subscription-picker__frequencies {
              max-height: 0;
              opacity: 0;
              transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
              margin-top: 0;
            }
            .subscription-picker__frequencies:not(.subscription-picker__frequencies--hidden) {
              max-height: 100px;
              opacity: 1;
              margin-top: 1rem;
            }
          </style>
        `

        this.setupEventListeners()
        this.findProductForm()
        this.updatePrice()
      }

      setupEventListeners() {
        this.checkbox = this.querySelector('input[type="checkbox"]')
        this.frequencyContainer = this.querySelector('.subscription-picker__frequencies')
        this.frequencyButtons = Array.from(this.querySelectorAll('.subscription-picker__frequency'))
        this.sellingPlanInput = this.querySelector('input[name="selling_plan"]')

        if (this.checkbox) {
          this.checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e))
          this.checkbox.addEventListener('keydown', (e) => this.handleCheckboxKeydown(e))
        }

        if (this.frequencyButtons.length) {
          this.frequencyButtons.forEach((button) => {
            button.addEventListener('click', (e) => this.handleFrequencyClick(e))
          })
        }

        this.updateVisibility()
      }

      handleCheckboxChange(event) {
        const isChecked = event.target.checked
        this.updateVisibility()

        if (!isChecked) {
          this.selectedSellingPlan = null
          this.updateSellingPlanInput()
          this.clearFrequencySelection()
          this.updatePrice()
        } else {
          const firstButton = this.frequencyButtons[0]
          if (firstButton) {
            const planId = parseInt(firstButton.dataset.planId)
            this.selectFrequency(firstButton, planId)
          }
        }
      }

      handleFrequencyClick(event) {
        const button = event.currentTarget
        const planId = parseInt(button.dataset.planId)
        this.selectFrequency(button, planId)
      }

      handleCheckboxKeydown(event) {
        if (event.key !== 'Enter') return

        event.preventDefault()
        this.checkbox.checked = !this.checkbox.checked
        this.checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }

      selectFrequency(button, planId) {
        this.selectedSellingPlan = planId
        this.updateSellingPlanInput()
        this.updateFrequencySelection(button)
        this.updatePrice()
      }

      updateFrequencySelection(selectedButton) {
        this.frequencyButtons.forEach((button) => {
          if (button === selectedButton) {
            button.classList.add('subscription-picker__frequency--selected')
            button.setAttribute('aria-pressed', 'true')
          } else {
            button.classList.remove('subscription-picker__frequency--selected')
            button.setAttribute('aria-pressed', 'false')
          }
        })
      }

      clearFrequencySelection() {
        this.frequencyButtons.forEach((button) => {
          button.classList.remove('subscription-picker__frequency--selected')
          button.setAttribute('aria-pressed', 'false')
        })
      }

      updateVisibility() {
        if (this.frequencyContainer) {
          const isExpanded = Boolean(this.checkbox && this.checkbox.checked)

          if (this.checkbox) {
            this.checkbox.setAttribute('aria-expanded', String(isExpanded))
          }

          if (isExpanded) {
            this.frequencyContainer.classList.remove('subscription-picker__frequencies--hidden')
            this.frequencyContainer.setAttribute('aria-hidden', 'false')
          } else {
            this.frequencyContainer.classList.add('subscription-picker__frequencies--hidden')
            this.frequencyContainer.setAttribute('aria-hidden', 'true')
          }

          this.frequencyButtons.forEach((button) => {
            button.tabIndex = isExpanded ? 0 : -1
          })
        }
      }

      updateSellingPlanInput() {
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = this.selectedSellingPlan || ''
        }
      }

      updatePrice() {
        const productInfo = this.closest('.product__info-container')
        if (!productInfo) return

        const sectionId = productInfo.dataset.section
        const priceElement = document.querySelector(`#product-form-${sectionId} .button__priced .priced`)
        if (!priceElement || !this.selectedVariant) return

        let price = this.selectedVariant.price

        if (this.selectedSellingPlan && this.checkbox?.checked) {
          const allocation = this.selectedVariant.selling_plan_allocations?.find(
            (alloc) => alloc.selling_plan_id === this.selectedSellingPlan
          )
          if (allocation && allocation.price !== undefined && allocation.price !== null) {
            price = allocation.price
          }
        }

        // Convert to integer cents - ensure no rounding occurs
        const priceInCents = Number.isInteger(price) ? price : Math.floor(Number(price))

        // Use Shopify formatMoney but force decimals
        const baseFormat = window.theme?.moneyFormat || window.Shopify?.theme?.moneyFormat || '${{amount}}'
        // Replace amount_no_decimals with amount to ensure decimals are shown
        const formatWithDecimals = baseFormat.replace(/amount_no_decimals/g, 'amount')

        if (typeof window.formatMoney === 'function') {
          let formattedPrice = window.formatMoney(priceInCents, formatWithDecimals)
          // Remove trailing zeros only if price is a whole number
          formattedPrice = formattedPrice.replace(/\.00$/, '')
          priceElement.textContent = formattedPrice
        } else {
          // Fallback: extract currency symbol from format and use basic formatting
          const currencyMatch = baseFormat.match(/^([^\{]*)\{\{/)
          const currencySymbol = currencyMatch ? currencyMatch[1].trim() : '$'
          const amount = (priceInCents / 100).toFixed(2)
          // Remove trailing zeros only if price is a whole number
          const formattedAmount = amount.replace(/\.00$/, '')
          priceElement.textContent = `${currencySymbol}${formattedAmount}`
        }
      }

      getDiscountPercentage(plan) {
        if (!plan?.price_adjustments || plan.price_adjustments.length === 0) return 0
        const adjustment = plan.price_adjustments[0]
        if (adjustment.value_type === 'percentage') {
          return adjustment.value
        }
        return 0
      }

      getFrequencyText(plan) {
        if (!plan.delivery_policy?.fixed_interval) return plan.name

        const intervalCount = plan.delivery_policy.fixed_interval.interval_count
        const intervalUnit = plan.delivery_policy.fixed_interval.interval_unit

        if (intervalUnit === 'DAY') {
          return `Every ${intervalCount} days`
        } else if (intervalUnit === 'WEEK') {
          return `Every ${intervalCount} ${intervalCount === 1 ? 'week' : 'weeks'}`
        } else if (intervalUnit === 'MONTH') {
          return `Every ${intervalCount} ${intervalCount === 1 ? 'month' : 'months'}`
        }

        return plan.name
      }
    }
  )
}
